import {
  BiometricSupport,
  BiometricCredential,
  BiometricAuthResult,
  BiometricRegistrationOptions,
  BiometricAuthenticationOptions,
  BiometricError,
  WebAuthnCredential,
  BiometricSettings
} from '../types/biometric';

class BiometricService {
  private static instance: BiometricService;
  private isInitialized = false;
  private support: BiometricSupport | null = null;

  private constructor() {}

  static getInstance(): BiometricService {
    if (!BiometricService.instance) {
      BiometricService.instance = new BiometricService();
    }
    return BiometricService.instance;
  }

  /**
   * Initialize the biometric service and check for support
   */
  async init(): Promise<BiometricSupport> {
    if (this.isInitialized && this.support) {
      return this.support;
    }

    try {
      const support = await this.checkSupport();
      this.support = support;
      this.isInitialized = true;
      return support;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.support = {
        isSupported: false,
        isAvailable: false,
        supportedAuthenticators: [],
        error: errorMessage
      };
      this.isInitialized = true;
      return this.support;
    }
  }

  /**
   * Check if biometric authentication is supported
   */
  private async checkSupport(): Promise<BiometricSupport> {
    // Check basic WebAuthn support
    if (!window.PublicKeyCredential) {
      return {
        isSupported: false,
        isAvailable: false,
        supportedAuthenticators: [],
        error: 'WebAuthn is not supported in this browser'
      };
    }

    try {
      // Check if platform authenticator is available (biometric)
      const platformAuthenticatorAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      
      // Check if conditional UI is supported (for autofill)
      const conditionalUISupported = await PublicKeyCredential.isConditionalMediationAvailable?.() || false;

      const supportedAuthenticators: Array<'platform' | 'cross-platform'> = [];
      
      if (platformAuthenticatorAvailable) {
        supportedAuthenticators.push('platform');
      }
      
      // Cross-platform authenticators (USB keys, etc.) are generally supported if WebAuthn is available
      supportedAuthenticators.push('cross-platform');

      return {
        isSupported: true,
        isAvailable: platformAuthenticatorAvailable || conditionalUISupported,
        supportedAuthenticators,
        error: undefined
      };
    } catch (error) {
      console.error('Error checking biometric support:', error);
      return {
        isSupported: false,
        isAvailable: false,
        supportedAuthenticators: [],
        error: error instanceof Error ? error.message : 'Failed to check biometric support'
      };
    }
  }

  /**
   * Get current biometric support status
   */
  async getSupport(): Promise<BiometricSupport> {
    if (!this.isInitialized) {
      return await this.init();
    }
    return this.support!;
  }

  /**
   * Register a new biometric credential
   */
  async registerCredential(nickname?: string, userInfo?: { username: string; email?: string; displayName?: string }): Promise<BiometricCredential> {
    const support = await this.getSupport();
    if (!support.isSupported || !support.isAvailable) {
      throw new Error('Biometric authentication is not available on this device');
    }

    try {
      // Use actual user information or fallback to defaults
      const userName = userInfo?.email || userInfo?.username || 'user@vaiu.io';
      const displayName = userInfo?.displayName || `${userInfo?.username || 'Vaiu User'}`;
      
      // Create the credential creation options
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        rp: {
          name: 'Vaiu',
          id: window.location.hostname,
        },
        user: {
          id: crypto.getRandomValues(new Uint8Array(64)),
          name: userName,
          displayName: displayName,
        },
        pubKeyCredParams: [
          {
            alg: -7, // ES256
            type: 'public-key',
          },
          {
            alg: -257, // RS256
            type: 'public-key',
          },
        ],
        timeout: 60000,
        attestation: 'none',
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'preferred',
        },
      };

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      // Prepare credential for backend
      const credentialData = {
        id: credential.id,
        rawId: this.arrayBufferToBase64url(credential.rawId),
        response: {
          attestationObject: this.arrayBufferToBase64url((credential.response as AuthenticatorAttestationResponse).attestationObject),
          clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
        },
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults()
      };

      // Register credential on backend
      const response = await fetch('/api/v1/auth/biometric/credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          nickname: nickname || `Device ${Date.now()}`,
          publicKeyCredential: credentialData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to register credential');
      }

      const result = await response.json();
      return result.credential;

    } catch (error) {
      console.error('Biometric registration error:', error);
      throw this.handleWebAuthnError(error);
    }
  }

  /**
   * Authenticate using biometric
   */
  async authenticate(credentialId?: string, signal?: AbortSignal, username?: string): Promise<BiometricAuthResult> {
    const support = await this.getSupport();
    if (!support.isSupported || !support.isAvailable) {
      return {
        success: false,
        error: 'Biometric authentication is not available on this device'
      };
    }

    try {
      // Create authentication options
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        timeout: 60000,
        rpId: window.location.hostname,
        userVerification: 'required'
      };

      // If specific credential ID is provided, use it
      if (credentialId) {
        publicKeyCredentialRequestOptions.allowCredentials = [{
          id: Uint8Array.from(atob(credentialId), c => c.charCodeAt(0)),
          type: 'public-key'
        }];
      }

      // Get credential with optional AbortSignal
      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
        signal
      }) as PublicKeyCredential;

      if (!credential) {
        return {
          success: false,
          error: 'Authentication was cancelled or failed'
        };
      }

      // Prepare credential for backend
      const credentialData = {
        id: credential.id,
        rawId: this.arrayBufferToBase64url(credential.rawId),
        response: {
          authenticatorData: this.arrayBufferToBase64url((credential.response as AuthenticatorAssertionResponse).authenticatorData),
          clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
          signature: this.arrayBufferToBase64url((credential.response as AuthenticatorAssertionResponse).signature),
          userHandle: (credential.response as AuthenticatorAssertionResponse).userHandle ? 
            this.arrayBufferToBase64url((credential.response as AuthenticatorAssertionResponse).userHandle!) : undefined
        },
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults()
      };

      // Authenticate with backend
      const requestBody: any = {
        publicKeyCredential: credentialData
      };
      
      // Include username if provided (needed for backend to identify user)
      if (username) {
        requestBody.username = username;
      }
      
      const response = await fetch('/api/v1/auth/biometric/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const result = await response.json();
      return {
        success: true,
        token: result.token,
        credentialId: credential.id
      };

    } catch (error) {
      console.error('Biometric authentication error:', error);
      return {
        success: false,
        error: this.handleWebAuthnError(error).message
      };
    }
  }

  /**
   * Authenticate using conditional UI (autofill)
   */
  async authenticateConditional(signal?: AbortSignal, username?: string): Promise<BiometricAuthResult> {
    const support = await this.getSupport();
    if (!support.isSupported) {
      return {
        success: false,
        error: 'Biometric authentication is not supported on this device'
      };
    }

    try {
      // Create authentication options for conditional UI
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        timeout: 600000, // 10 minutes for autofill
        rpId: window.location.hostname,
        userVerification: 'preferred'
      };

      // Get credential with conditional mediation
      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
        mediation: 'conditional',
        signal
      }) as PublicKeyCredential;

      if (!credential) {
        return {
          success: false,
          error: 'Authentication was cancelled or failed'
        };
      }

      // Process credential same as regular authentication
      const credentialData = {
        id: credential.id,
        rawId: this.arrayBufferToBase64url(credential.rawId),
        response: {
          authenticatorData: this.arrayBufferToBase64url((credential.response as AuthenticatorAssertionResponse).authenticatorData),
          clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
          signature: this.arrayBufferToBase64url((credential.response as AuthenticatorAssertionResponse).signature),
          userHandle: (credential.response as AuthenticatorAssertionResponse).userHandle ? 
            this.arrayBufferToBase64url((credential.response as AuthenticatorAssertionResponse).userHandle!) : undefined
        },
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults()
      };

      // Authenticate with backend
      const requestBody: any = {
        publicKeyCredential: credentialData
      };
      
      // Include username if provided (needed for backend to identify user)
      if (username) {
        requestBody.username = username;
      }
      
      const response = await fetch('/api/v1/auth/biometric/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const result = await response.json();
      return {
        success: true,
        token: result.token,
        credentialId: credential.id
      };

    } catch (error: any) {
      // AbortError is expected when cancelling or unmounting; keep console clean
      if (error?.name === 'AbortError') {
        return {
          success: false,
          error: 'Biometric authentication was cancelled'
        };
      }

      // NotAllowedError is common when user dismisses or conditional UI has no credentials
      if (error?.name === 'NotAllowedError') {
        console.debug('Conditional biometric authentication dismissed or not allowed');
        return {
          success: false,
          error: 'Biometric authentication cancelled'
        };
      }

      console.error('Conditional biometric authentication error:', error);
      return {
        success: false,
        error: this.handleWebAuthnError(error).message
      };
    }
  }

  /**
   * Get user's biometric settings
   */
  async getBiometricSettings(): Promise<BiometricSettings> {
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Don't even try if there's no auth token
      if (!authToken) {
        console.log('No auth token available for biometric settings');
        return {
          enabled: false,
          requireForLogin: false,
          requireForTransactions: false,
          credentials: []
        };
      }

      const response = await fetch('/api/v1/auth/biometric/settings', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.status === 401) {
        console.log('Unauthorized - user not logged in for biometric settings');
        return {
          enabled: false,
          requireForLogin: false,
          requireForTransactions: false,
          credentials: []
        };
      }

      if (!response.ok) {
        throw new Error('Failed to get biometric settings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting biometric settings:', error);
      // Return default settings if API fails
      return {
        enabled: false,
        requireForLogin: false,
        requireForTransactions: false,
        credentials: []
      };
    }
  }

  /**
   * Update biometric settings
   */
  async updateBiometricSettings(settings: Partial<BiometricSettings>): Promise<void> {
    try {
      const response = await fetch('/api/v1/auth/biometric/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error('Failed to update biometric settings');
      }
    } catch (error) {
      console.error('Error updating biometric settings:', error);
      throw error;
    }
  }

  /**
   * Delete a biometric credential
   */
  async deleteCredential(credentialId: string): Promise<void> {
    try {
      const response = await fetch(`/api/v1/auth/biometric/credentials/${credentialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete credential');
      }
    } catch (error) {
      console.error('Error deleting credential:', error);
      throw error;
    }
  }

  /**
   * Update credential nickname
   */
  async updateCredentialNickname(credentialId: string, nickname: string): Promise<void> {
    try {
      const response = await fetch(`/api/v1/auth/biometric/credentials/${credentialId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ nickname })
      });

      if (!response.ok) {
        throw new Error('Failed to update credential nickname');
      }
    } catch (error) {
      console.error('Error updating credential nickname:', error);
      throw error;
    }
  }

  /**
   * Helper method to convert ArrayBuffer to base64url
   */
  private arrayBufferToBase64url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (let i = 0; i < bytes.length; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Handle WebAuthn-specific errors and convert them to user-friendly messages
   */
  private handleWebAuthnError(error: any): Error {
    // Silence AbortError logs; they are expected during cancellation/unmount
    if (error?.name !== 'AbortError') {
      console.error('WebAuthn error:', error);
    }
    
    // Handle specific WebAuthn error types
    if (error.name === 'NotAllowedError') {
      return new Error('Biometric authentication was cancelled or not allowed');
    }
    
    if (error.name === 'AbortError') {
      return new Error('Biometric authentication was cancelled');
    }
    
    if (error.name === 'NotSupportedError') {
      return new Error('Biometric authentication is not supported on this device');
    }
    
    if (error.name === 'SecurityError') {
      return new Error('Biometric authentication failed due to security restrictions');
    }
    
    if (error.name === 'InvalidStateError') {
      return new Error('Biometric authentication is not available in current state');
    }
    
    if (error.name === 'ConstraintError') {
      return new Error('Biometric authentication constraints could not be satisfied');
    }
    
    if (error.name === 'UnknownError') {
      return new Error('An unknown error occurred during biometric authentication');
    }
    
    // Handle network/API errors
    if (error.message?.includes('Failed to fetch')) {
      return new Error('Network error during biometric authentication');
    }
    
    // Default error handling
    return new Error(error.message || 'Biometric authentication failed');
  }
}

// Export singleton instance
export const biometricService = BiometricService.getInstance();
export default biometricService;